import { sha256 } from '../lib/crypto';
import type {
  DockBoxesState,
  FerryFourState,
  GameKind,
  GameMove,
  GameState,
  GameWinner,
  PlayerSide,
  SignalChoice,
  SignalShowdownState,
  TicTacToeState,
} from './types';

const encoder = new TextEncoder();
const otherSide = (side: PlayerSide): PlayerSide => side === 'host' ? 'guest' : 'host';

export function createGameState(sessionId: string, kind: GameKind, startingPlayer: PlayerSide): GameState {
  const base = { sessionId, kind, status: 'playing' as const, moveNumber: 0, startingPlayer };
  if (kind === 'tic-tac-toe') return { ...base, kind, board: Array<PlayerSide | null>(9).fill(null), turn: startingPlayer };
  if (kind === 'ferry-four') return { ...base, kind, board: Array<PlayerSide | null>(42).fill(null), turn: startingPlayer };
  if (kind === 'dock-boxes') {
    return {
      ...base,
      kind,
      horizontal: Array<PlayerSide | null>(20).fill(null),
      vertical: Array<PlayerSide | null>(20).fill(null),
      boxes: Array<PlayerSide | null>(16).fill(null),
      scores: { host: 0, guest: 0 },
      turn: startingPlayer,
    };
  }
  return {
    ...base,
    kind,
    round: 1,
    scores: { host: 0, guest: 0 },
    commitments: {},
    reveals: {},
    ready: {},
  };
}

export async function signalCommitment(sessionId: string, round: number, choice: SignalChoice, nonce: string): Promise<string> {
  return sha256(encoder.encode(`${sessionId}:${round}:${choice}:${nonce}`));
}

export async function applyGameMove(state: GameState, actor: PlayerSide, move: GameMove): Promise<GameState> {
  if (state.status === 'complete') throw new Error('This game has finished.');
  if (state.kind === 'tic-tac-toe' && move.type === 'tic-cell') return applyTicMove(state, actor, move.index);
  if (state.kind === 'ferry-four' && move.type === 'ferry-column') return applyFerryMove(state, actor, move.column);
  if (state.kind === 'dock-boxes' && move.type === 'dock-edge') return applyDockMove(state, actor, move.orientation, move.index);
  if (state.kind === 'signal-showdown') return applySignalMove(state, actor, move);
  throw new Error('That move does not belong to this game.');
}

function applyTicMove(state: TicTacToeState, actor: PlayerSide, index: number): TicTacToeState {
  requireTurn(state.turn, actor);
  if (!Number.isInteger(index) || index < 0 || index >= state.board.length || state.board[index]) throw new Error('That space is not available.');
  const board = [...state.board];
  board[index] = actor;
  const lines = [[0, 1, 2], [3, 4, 5], [6, 7, 8], [0, 3, 6], [1, 4, 7], [2, 5, 8], [0, 4, 8], [2, 4, 6]];
  const won = lines.some((line) => line.every((cell) => board[cell] === actor));
  const winner: GameWinner | undefined = won ? actor : board.every(Boolean) ? 'draw' : undefined;
  return { ...state, board, turn: otherSide(actor), moveNumber: state.moveNumber + 1, status: winner ? 'complete' : 'playing', winner };
}

function applyFerryMove(state: FerryFourState, actor: PlayerSide, column: number): FerryFourState {
  requireTurn(state.turn, actor);
  if (!Number.isInteger(column) || column < 0 || column > 6) throw new Error('That column is invalid.');
  let row = 5;
  while (row >= 0 && state.board[row * 7 + column]) row -= 1;
  if (row < 0) throw new Error('That column is full.');
  const board = [...state.board];
  board[row * 7 + column] = actor;
  const won = [[1, 0], [0, 1], [1, 1], [1, -1]].some(([rowStep, columnStep]) => {
    let count = 1;
    for (const direction of [-1, 1]) {
      let nextRow = row + rowStep * direction;
      let nextColumn = column + columnStep * direction;
      while (nextRow >= 0 && nextRow < 6 && nextColumn >= 0 && nextColumn < 7 && board[nextRow * 7 + nextColumn] === actor) {
        count += 1;
        nextRow += rowStep * direction;
        nextColumn += columnStep * direction;
      }
    }
    return count >= 4;
  });
  const winner: GameWinner | undefined = won ? actor : board.every(Boolean) ? 'draw' : undefined;
  return { ...state, board, turn: otherSide(actor), moveNumber: state.moveNumber + 1, status: winner ? 'complete' : 'playing', winner };
}

function applyDockMove(
  state: DockBoxesState,
  actor: PlayerSide,
  orientation: 'horizontal' | 'vertical',
  index: number,
): DockBoxesState {
  requireTurn(state.turn, actor);
  if (!Number.isInteger(index) || index < 0 || index >= 20) throw new Error('That dock edge is invalid.');
  const horizontal = [...state.horizontal];
  const vertical = [...state.vertical];
  const edges = orientation === 'horizontal' ? horizontal : vertical;
  if (edges[index]) throw new Error('That dock edge is already drawn.');
  edges[index] = actor;

  const boxes = [...state.boxes];
  let completed = 0;
  for (let row = 0; row < 4; row += 1) {
    for (let column = 0; column < 4; column += 1) {
      const box = row * 4 + column;
      if (boxes[box]) continue;
      const enclosed = Boolean(
        horizontal[row * 4 + column]
        && horizontal[(row + 1) * 4 + column]
        && vertical[row * 5 + column]
        && vertical[row * 5 + column + 1],
      );
      if (enclosed) {
        boxes[box] = actor;
        completed += 1;
      }
    }
  }
  const scores = { ...state.scores, [actor]: state.scores[actor] + completed };
  const finished = boxes.every(Boolean);
  const winner: GameWinner | undefined = finished
    ? scores.host === scores.guest ? 'draw' : scores.host > scores.guest ? 'host' : 'guest'
    : undefined;
  return {
    ...state,
    horizontal,
    vertical,
    boxes,
    scores,
    turn: completed > 0 ? actor : otherSide(actor),
    moveNumber: state.moveNumber + 1,
    status: finished ? 'complete' : 'playing',
    winner,
  };
}

async function applySignalMove(state: SignalShowdownState, actor: PlayerSide, move: GameMove): Promise<SignalShowdownState> {
  if (move.type === 'signal-commit') {
    if (move.round !== state.round || !/^[A-Za-z0-9_-]{43}$/u.test(move.commitment)) throw new Error('That signal commitment is invalid.');
    if (state.commitments[actor]) throw new Error('That player has already locked in a signal.');
    return { ...state, commitments: { ...state.commitments, [actor]: move.commitment }, moveNumber: state.moveNumber + 1 };
  }
  if (move.type === 'signal-reveal') {
    if (move.round !== state.round || !state.commitments.host || !state.commitments.guest || state.reveals[actor]) {
      throw new Error('That signal cannot be revealed yet.');
    }
    if (!['rock', 'paper', 'scissors'].includes(move.choice) || !/^[A-Za-z0-9_-]{16,64}$/u.test(move.nonce)) throw new Error('That signal reveal is invalid.');
    const expected = await signalCommitment(state.sessionId, state.round, move.choice, move.nonce);
    if (expected !== state.commitments[actor]) throw new Error('The revealed signal did not match its locked choice.');
    const reveals = { ...state.reveals, [actor]: move.choice };
    if (!reveals.host || !reveals.guest) return { ...state, reveals, moveNumber: state.moveNumber + 1 };
    const roundWinner = signalWinner(reveals.host, reveals.guest);
    const scores = { ...state.scores };
    if (roundWinner !== 'draw') scores[roundWinner] += 1;
    const winner = scores.host >= 3 ? 'host' : scores.guest >= 3 ? 'guest' : undefined;
    return {
      ...state,
      reveals,
      scores,
      roundWinner,
      moveNumber: state.moveNumber + 1,
      status: winner ? 'complete' : 'playing',
      winner,
    };
  }
  if (move.type === 'signal-next') {
    if (move.round !== state.round || !state.reveals.host || !state.reveals.guest || state.ready[actor]) throw new Error('The next round is not ready.');
    const ready = { ...state.ready, [actor]: true as const };
    if (!ready.host || !ready.guest) return { ...state, ready, moveNumber: state.moveNumber + 1 };
    return {
      ...state,
      round: state.round + 1,
      commitments: {},
      reveals: {},
      ready: {},
      roundWinner: undefined,
      moveNumber: state.moveNumber + 1,
    };
  }
  throw new Error('That move does not belong to Signal Showdown.');
}

function signalWinner(host: SignalChoice, guest: SignalChoice): GameWinner {
  if (host === guest) return 'draw';
  if (
    (host === 'rock' && guest === 'scissors')
    || (host === 'paper' && guest === 'rock')
    || (host === 'scissors' && guest === 'paper')
  ) return 'host';
  return 'guest';
}

function requireTurn(turn: PlayerSide, actor: PlayerSide): void {
  if (turn !== actor) throw new Error('It is not that player’s turn.');
}
